import React, { useEffect, useState, useCallback, useRef, Component } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers, faHandshake, faUserCheck, faChartLine,
  faExclamationTriangle, faCheckCircle,
  faSyncAlt, faSearch, faTimes, faFilter,
  faCalendarAlt, faFileDownload, faBullhorn, faArrowUp,
  faStar, faPauseCircle, faMinusCircle, faFire,
  faMoneyBillWave, faPercentage, faTrophy, faChartPie,
  faArrowDown, faBullseye, faClock, faPhoneVolume, faLock,
} from '@fortawesome/free-solid-svg-icons'
import Layout from '../../../../app/Layout'
import HeaderContent from '../../General/HeaderContent'
import Breadcrumb from '../../General/Breadcrumb'
import SpinnerLoading from '../../../components/Spinners/SpinnerLoading'
import authAxios from '../../../../config/authAxios'

/* ── helpers ─────────────────────────────────────────────────────────────── */
const fmt    = n => Number(n || 0).toLocaleString('es-PE')
const fmtS   = n => `S/ ${Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const mesLbl = ym => {
  if (!ym) return ''
  const [y, m] = ym.split('-')
  const N = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']
  return `${N[+m]} ${y}`
}

/* ── colores ─────────────────────────────────────────────────────────────── */
const C_V = '#28a745'
const C_A = '#fd7e14'
const C_R = '#dc3545'
const C_G = '#6c757d'
const C_B = '#4a6cf7'
const C_P = '#6f42c1'

const semVerde  = (v, meta, inv = false) => inv ? (v <= meta ? C_V : v <= meta * 2 ? C_A : C_R) : (v >= meta ? C_V : v >= meta * 0.6 ? C_A : C_R)
const semROI    = v => v >= 100 ? C_V : v >= 0 ? C_A : C_R
const semCPApct = v => v === 0 ? C_G : v <= 5 ? C_V : v <= 10 ? C_A : C_R

/* ── Pill ────────────────────────────────────────────────────────────────── */
function Pill({ color, children, size = 12 }) {
  const c = color || C_G
  return (
    <span style={{ fontSize: size, fontWeight: 600, color: c, backgroundColor: `${c}18`, padding: '2px 10px', borderRadius: 20 }}>
      {children}
    </span>
  )
}

/* ── Dot ─────────────────────────────────────────────────────────────────── */
function Dot({ color, size = 10 }) {
  return <span style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', backgroundColor: color || C_G, flexShrink: 0 }} />
}

/* ── VarBadge (variación mes a mes) ─────────────────────────────────────── */
function VarBadge({ delta }) {
  if (delta === null || delta === undefined) return null
  const up = delta >= 0
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      color: up ? C_V : C_R,
      backgroundColor: up ? `${C_V}18` : `${C_R}18`,
      padding: '2px 6px', borderRadius: 12,
      display: 'inline-flex', alignItems: 'center', gap: 2,
      marginBottom: 3, flexShrink: 0,
    }}>
      {up ? '↑' : '↓'}{Math.abs(delta)}%
    </span>
  )
}

/* ── Tooltip ─────────────────────────────────────────────────────────────── */
function Tooltip({ children, text }) {
  const [vis, setVis] = useState(false)
  if (!text) return children
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setVis(true)} onMouseLeave={() => setVis(false)}>
      {children}
      {vis && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#222', color: '#fff',
          padding: '5px 10px', borderRadius: 6, fontSize: 11,
          whiteSpace: 'nowrap', zIndex: 9999,
          boxShadow: '0 2px 8px rgba(0,0,0,.3)', pointerEvents: 'none',
        }}>
          {text}
          <span style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            border: '4px solid transparent', borderTopColor: '#222',
          }} />
        </span>
      )}
    </span>
  )
}

/* ── SortTh ──────────────────────────────────────────────────────────────── */
function SortTh({ col, sortCol, sortDir, onSort, children, style }) {
  const active = sortCol === col
  return (
    <th onClick={() => onSort(col)} style={{ cursor: 'pointer', userSelect: 'none', ...style }}>
      {children}
      <span style={{ marginLeft: 3, fontSize: 9, opacity: active ? 1 : 0.25 }}>
        {active ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}
      </span>
    </th>
  )
}

/* ── VelocidadCard ───────────────────────────────────────────────────────── */
function VelocidadCard({ titulo, dias, umbrales, labels, tip }) {
  const color = dias <= umbrales[0] ? C_V : dias <= umbrales[1] ? C_A : C_R
  const label = dias <= umbrales[0] ? labels[0] : dias <= umbrales[1] ? labels[1] : labels[2]
  const pct   = Math.min(dias / (umbrales[1] * 2) * 100, 100)
  return (
    <div className="box h-100 mb-0" style={{ borderTop: `4px solid ${color}`, padding: '16px 18px' }}>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <span style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>{titulo}</span>
        <Pill color={color} size={11}>{label}</Pill>
      </div>
      <div style={{ lineHeight: 1, marginBottom: 12 }}>
        <span style={{ fontSize: 34, fontWeight: 800, color }}>{dias}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#888', marginLeft: 6 }}>días prom.</span>
      </div>
      <div style={{ height: 6, backgroundColor: '#e9ecef', borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: 3, transition: 'width .4s ease' }} />
      </div>
      <div style={{ fontSize: 10, color: '#aaa' }}>{tip}</div>
    </div>
  )
}

/* ── ProgramCombobox ─────────────────────────────────────────────────────── */
function ProgramCombobox({ proyectos, pidSel, onChange }) {
  const [open,   setOpen]   = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef(null)

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const selected = proyectos.find(p => p.id_proyecto === pidSel)
  const filtered = proyectos.filter(p =>
    p.nombre_proyecto.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 320, maxWidth: 480 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px', border: '1.5px solid #dee2e6', borderRadius: 8,
          background: '#fff', cursor: 'pointer', userSelect: 'none',
          boxShadow: open ? '0 0 0 3px rgba(13,110,253,.15)' : 'none',
          borderColor: open ? '#86b7fe' : '#dee2e6',
        }}
      >
        <span style={{ fontSize: 14, color: selected ? '#212529' : '#adb5bd', fontWeight: selected ? 600 : 400 }}>
          {selected ? selected.nombre_proyecto : 'Selecciona un programa…'}
        </span>
        <FontAwesomeIcon icon={open ? faTimes : faSearch} style={{ color: '#6c757d', fontSize: 13, marginLeft: 8 }} />
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '110%', left: 0, right: 0, zIndex: 9999,
          background: '#fff', border: '1.5px solid #dee2e6', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.12)', overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0' }}>
            <input
              autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar programa…"
              style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, background: 'transparent' }}
            />
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {filtered.length === 0
              ? <div style={{ padding: '12px 14px', color: '#adb5bd', fontSize: 13 }}>Sin resultados</div>
              : filtered.map(p => (
                <div
                  key={p.id_proyecto}
                  onClick={() => { onChange(p.id_proyecto); setOpen(false); setSearch('') }}
                  style={{
                    padding: '10px 14px', cursor: 'pointer', fontSize: 13,
                    background: p.id_proyecto === pidSel ? '#e8f0fe' : '#fff',
                    fontWeight: p.id_proyecto === pidSel ? 700 : 400,
                    borderBottom: '1px solid #f8f8f8',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = p.id_proyecto === pidSel ? '#e8f0fe' : '#f8f9fa'}
                  onMouseLeave={e => e.currentTarget.style.background = p.id_proyecto === pidSel ? '#e8f0fe' : '#fff'}
                >
                  <div style={{ color: '#212529' }}>{p.nombre_proyecto}</div>
                  <div style={{ fontSize: 11, color: '#adb5bd', marginTop: 2 }}>{fmt(p.total_leads)} leads</div>
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  )
}

/* ── ErrorBoundary ───────────────────────────────────────────────────────── */
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(err) { return { error: err } }
  render() {
    if (this.state.error) return (
      <div className="alert alert-danger m-3" style={{ fontFamily: 'monospace', fontSize: 12 }}>
        <strong>Error de renderizado:</strong> {this.state.error?.message}
      </div>
    )
    return this.props.children
  }
}

/* ── Gráfico histórico ───────────────────────────────────────────────────── */
function ChartHistorico({ data, xField = 'mes', labelFn = mesLbl, showRevenue = true }) {
  if (!data?.length) return (
    <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', borderRadius: 6, border: '1px dashed #dee2e6' }}>
      <div className="text-center">
        <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 32, color: '#ced4da', display: 'block', margin: '0 auto 10px' }} />
        <p className="text-muted mb-0" style={{ fontSize: 13, fontWeight: 600 }}>Sin datos históricos</p>
      </div>
    </div>
  )
  const cats = data.map(d => labelFn(d[xField]))
  const series = [
    { name: 'Leads',     data: data.map(d => +d.leads     || 0), color: C_B,  yAxis: 0 },
    { name: 'Tratos',    data: data.map(d => +d.tratos    || 0), color: C_A,  yAxis: 0 },
    { name: 'Inscritos', data: data.map(d => +d.inscritos || 0), color: C_V,  yAxis: 0 },
  ]
  if (showRevenue) {
    series.push({ name: 'Revenue', data: data.map(d => +d.revenue || 0), color: C_P, yAxis: 1, lineWidth: 1.5, dashStyle: 'ShortDash', marker: { radius: 3 } })
  }
  const options = {
    chart: { type: 'line', height: 230, backgroundColor: 'transparent', animation: false, style: { fontFamily: 'inherit' } },
    title: { text: null }, credits: { enabled: false },
    legend: { enabled: true, align: 'center', verticalAlign: 'top', itemStyle: { fontSize: '11px', fontWeight: '600' } },
    xAxis: { categories: cats, labels: { style: { fontSize: '11px', color: '#888' } }, tickLength: 0, lineColor: '#e9ecef' },
    yAxis: [
      { title: { text: null }, gridLineColor: '#f0f0f0', labels: { style: { fontSize: '11px', color: '#888' } }, min: 0 },
      { title: { text: null }, opposite: true, labels: { style: { fontSize: '11px', color: '#aaa' }, formatter: function() { return 'S/'+this.value } }, min: 0, gridLineWidth: 0, visible: showRevenue },
    ],
    tooltip: { shared: true, style: { fontSize: '12px' } },
    plotOptions: { line: { marker: { radius: 4, symbol: 'circle' }, lineWidth: 2.5 } },
    series,
  }
  return <HighchartsReact key={(data[0]?.[xField] || '') + data.length} highcharts={Highcharts} options={options} />
}

const diaLbl = ymd => {
  if (!ymd) return ''
  const [, m, d] = ymd.split('-')
  const N = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']
  return `${d} ${N[+m]}`
}

/* ── Badges de acción ────────────────────────────────────────────────────── */
const ACCION_CFG = {
  escalar:  { label: 'Escalar',  color: C_V,       icon: faArrowUp      },
  mantener: { label: 'Mantener', color: '#17a2b8',  icon: faMinusCircle  },
  pausar:   { label: 'Pausar',   color: C_R,       icon: faPauseCircle  },
  revisar:  { label: 'Revisar',  color: C_A,       icon: faExclamationTriangle },
}
const ESTADO_FUENTE = ACCION_CFG

function AccionBadge({ accion }) {
  const cfg = ACCION_CFG[accion] || ACCION_CFG.revisar
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, backgroundColor: `${cfg.color}18`, padding: '3px 10px', borderRadius: 20, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <FontAwesomeIcon icon={cfg.icon} style={{ fontSize: 10 }} />
      {cfg.label}
    </span>
  )
}

/* ── KPI card ────────────────────────────────────────────────────────────── */
function KpiCard({ label, sub, valor, meta, color, icon, detalle, small = false, variacion = null }) {
  return (
    <div className="box h-100" style={{ borderTop: `4px solid ${color}`, padding: '16px 18px' }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: .5 }}>{label}</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <FontAwesomeIcon icon={icon} style={{ color, fontSize: 14 }} />
        </div>
      </div>
      <div className="d-flex align-items-end flex-wrap" style={{ gap: 6 }}>
        <div style={{ fontSize: small ? 20 : 28, fontWeight: 800, color, lineHeight: 1.1 }}>{valor}</div>
        <VarBadge delta={variacion} />
      </div>
      <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>{sub}</div>
      {meta && <div style={{ marginTop: 6 }}><Pill color={color} size={11}>{meta}</Pill></div>}
      {detalle && <div style={{ fontSize: 12, color: '#666', marginTop: 5 }}>{detalle}</div>}
    </div>
  )
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  COMPONENTE PRINCIPAL                                                      */
/* ══════════════════════════════════════════════════════════════════════════ */
const ReporteKpisMarketing = () => {
  const [proyectos,   setProyectos]   = useState([])
  const [pidSel,      setPidSel]      = useState(null)
  const [mesFilter,   setMesFilter]   = useState('')
  const [data,        setData]        = useState(null)
  const [loading,     setLoading]     = useState(false)
  const [error,       setError]       = useState(null)
  const [loadingProy, setLoadingProy] = useState(true)
  const [sortCol,     setSortCol]     = useState('inversion')
  const [sortDir,     setSortDir]     = useState('desc')
  const [syncing,     setSyncing]     = useState(false)
  const [syncMsg,     setSyncMsg]     = useState(null)
  const [modalPieza,  setModalPieza]  = useState(null)
  const [vistaCarrusel, setVistaCarrusel] = useState(false)
  const [carrPos,     setCarrPos]     = useState(0)
  const carrRef = useRef(null)
  const [filtroPlat,   setFiltroPlat]   = useState('todos')
  const [filtroTipo,   setFiltroTipo]   = useState('todos')
  const [filtroAccion, setFiltroAccion] = useState('todos')
  const [filtroFuente, setFiltroFuente] = useState('todos')
  const [periodoHist,  setPeriodoHist]  = useState('mensual')
  const [hoveredRow,   setHoveredRow]   = useState(null)

  // ── Bitácora de Marketing (criterio 25 HU-16) ──────────────────────────────
  const [bitacora,     setBitacora]     = useState([])
  const [kpisBitacora, setKpisBitacora] = useState([])
  const [bitLoading,   setBitLoading]   = useState(false)
  const [bitForm,      setBitForm]      = useState({ tipo: 'tarea', contenido: '', kpi_afectado: '', fecha: new Date().toISOString().slice(0, 10) })
  const [bitSaving,    setBitSaving]    = useState(false)
  const [bitMsg,       setBitMsg]       = useState(null)

  // Resetear hover y posición de carrusel cuando cambian los filtros
  useEffect(() => { setHoveredRow(null) }, [filtroPlat, filtroTipo, filtroAccion, filtroFuente])
  useEffect(() => {
    setCarrPos(0)
    if (carrRef.current) {
      const t = carrRef.current.querySelector('[data-carr-track]')
      if (t) t.style.transform = 'translateX(0px)'
    }
  }, [filtroPlat, filtroTipo, filtroAccion, filtroFuente])

  useEffect(() => {
    authAxios.get('/admin/kpis-marketing/proyectos')
      .then(r => {
        const lista = r.data?.data || []
        setProyectos(lista)
        if (lista.length) setPidSel(lista[0].id_proyecto)
      })
      .catch(() => {})
      .finally(() => setLoadingProy(false))
  }, [])

  const cargarData = useCallback((pid, mes) => {
    if (!pid) return
    setLoading(true); setError(null)
    const params = [`id_proyecto=${pid}`, mes && `mes=${mes}`].filter(Boolean).join('&')
    authAxios.get(`/admin/kpis-marketing/data?${params}`)
      .then(r => {
        if (r.data?.error) { setError(r.data.error); setData(null) }
        else setData(r.data)
      })
      .catch(e => { setError(e.response?.data?.error || e.message || 'Error de conexión'); setData(null) })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { cargarData(pidSel, mesFilter) }, [pidSel, mesFilter, cargarData])

  const cargarBitacora = useCallback(pid => {
    if (!pid) return
    setBitLoading(true)
    authAxios.get(`/admin/kpis-marketing/bitacora?id_proyecto=${pid}`)
      .then(r => {
        setBitacora(r.data?.data || [])
        setKpisBitacora(r.data?.kpis || [])
      })
      .catch(() => { setBitacora([]); setKpisBitacora([]) })
      .finally(() => setBitLoading(false))
  }, [])

  useEffect(() => { cargarBitacora(pidSel) }, [pidSel, cargarBitacora])

  const guardarBitacora = async () => {
    if (!pidSel || !bitForm.contenido.trim()) return
    setBitSaving(true); setBitMsg(null)
    try {
      const r = await authAxios.post('/admin/kpis-marketing/bitacora/guardar', { id_producto: pidSel, ...bitForm })
      if (r.data?.ok) {
        setBitForm(f => ({ ...f, contenido: '', kpi_afectado: '' }))
        setBitMsg({ ok: true, text: 'Registrado' })
        cargarBitacora(pidSel)
        setTimeout(() => setBitMsg(null), 1500)
      } else setBitMsg({ ok: false, text: r.data?.error || 'Error al guardar' })
    } catch { setBitMsg({ ok: false, text: 'Error de conexión' }) }
    finally { setBitSaving(false) }
  }

  const cambiarEstadoBit = async (id, estado) => {
    setBitacora(b => b.map(x => x.id === id ? { ...x, estado } : x))
    try { await authAxios.post('/admin/kpis-marketing/bitacora/estado', { id, estado }) } catch { cargarBitacora(pidSel) }
  }

  const eliminarBit = async id => {
    setBitacora(b => b.filter(x => x.id !== id))
    try { await authAxios.post('/admin/kpis-marketing/bitacora/eliminar', { id }) } catch { cargarBitacora(pidSel) }
  }

  const resumen    = data?.resumen    || {}
  const inversion  = data?.inversion  || {}
  const eficiencia = data?.eficiencia || {}
  const fuentes    = data?.fuentes    || []
  const piezas     = data?.piezas     || []
  const historico  = data?.historico  || []
  const diagnostico = data?.diagnostico || null
  const objetivoGeneral = data?.objetivo_general || null
  const colorRitmoObj = (v) => v == null ? C_G : v >= 0 ? C_V : v >= -15 ? C_A : C_R
  const historicoDiario  = data?.historico_diario  || []
  const historicoSemanal = data?.historico_semanal || []
  const historicoFuente  = data?.historico_fuente  || []
  const historicoPieza   = data?.historico_pieza   || []
  const variacion   = data?.variacion   || {}
  const velocidad   = data?.velocidad   || {}
  const sync        = data?.sync        || {}

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortCol(col); setSortDir('desc') }
  }
  const sortedPiezas = [...piezas].sort((a, b) => {
    const va = a[sortCol] != null ? +a[sortCol] : -1
    const vb = b[sortCol] != null ? +b[sortCol] : -1
    return sortDir === 'desc' ? vb - va : va - vb
  })
  const filteredPiezas = sortedPiezas.filter(p => {
    if (filtroPlat   !== 'todos' && p.pagina        !== filtroPlat)   return false
    if (filtroTipo   !== 'todos' && p.tipo_anuncio  !== filtroTipo)   return false
    if (filtroAccion !== 'todos' && p.accion        !== filtroAccion) return false
    if (filtroFuente !== 'todos' && p.nombre_fuente !== filtroFuente) return false
    return true
  })
  const fuentesPiezas = [...new Set(piezas.map(p => p.nombre_fuente).filter(Boolean))]
  const topScore = [...piezas].filter(p => p.score !== null && p.score > 0).sort((a,b) => b.score - a.score)[0] || null
  const topCTR   = [...piezas].filter(p => p.ctr   > 0).sort((a,b)          => b.ctr   - a.ctr)[0]   || null
  const topLeads = [...piezas].filter(p => p.cantidad_leads > 0).sort((a,b) => b.cantidad_leads - a.cantidad_leads)[0] || null
  const prodActual = proyectos.find(p => p.id_proyecto === pidSel)

  const calidad = +resumen.calidad_pct   || 0
  const conv    = +resumen.conversion_pct || 0
  const META_CALIDAD = 5
  const META_CONV    = 5
  const META_CPA_PCT = 5

  /* ── exportar CSV fuentes ──────────────────────────────────────────────── */
  const exportarCSV = () => {
    const lineas = [
      ['== FUENTES =='],
      ['Fuente','Leads','Tratos','Calidad%','Inscritos','Conv%','Revenue','Inv.Atrib.','CPA','ROI%','Estado'],
      ...fuentes.map(f => [f.nombre_fuente,f.leads,f.tratos,f.pct_calidad,f.inscritos,f.pct_conv,f.revenue_fuente,f.inv_atribuida,f.cpa_fuente,f.roi_fuente,f.estado]),
      [''],
      ['== PIEZAS =='],
      ['Nombre','Tipo','Plataforma','Inversión','Leads','CPA Lead','CTR%','Score','Acción'],
      ...piezas.map(p => [p.nombre_anuncio,p.tipo_anuncio,p.pagina,p.inversion,p.cantidad_leads,p.costo_lead,p.ctr ?? '',p.score ?? '',p.accion]),
    ]
    const q    = s => `"${String(s ?? '').replace(/"/g, '""')}"`
    const rows = lineas.map(r => r.map(c => q(c)).join(',')).join('\n')
    const blob = new Blob(['﻿' + rows], { type: 'text/csv;charset=utf-8' })
    const url  = URL.createObjectURL(blob)
    const el   = document.createElement('a')
    el.href = url
    el.download = `kpis-mkt-${(prodActual?.nombre_proyecto || 'datos').slice(0, 30)}-${new Date().toISOString().slice(0,10)}.csv`
    el.click(); URL.revokeObjectURL(url)
  }

  const runSync = async () => {
    setSyncing(true); setSyncMsg(null)
    try {
      await authAxios.get('/admin/ad-sync/facebook')
      await authAxios.get('/admin/ad-sync/tiktok')
      setSyncMsg({ ok: true, text: 'Sync completado — recargando datos...' })
      setTimeout(() => { cargarData(pidSel, mesFilter); setSyncMsg(null) }, 1800)
    } catch (e) {
      setSyncMsg({ ok: false, text: 'Error al sincronizar: ' + (e.response?.data?.msg || e.message) })
    } finally {
      setSyncing(false)
    }
  }

  const getVimeoSrc = (raw) => {
    if (!raw) return null
    raw = raw.trim()
    let id = null
    if (/<iframe/i.test(raw)) {
      const m = raw.match(/src=["']([^"']+)["']/)
      if (m) return m[1].split('?')[0] + '?muted=1&autoplay=1&loop=1&background=0&autopause=0'
    }
    const m = raw.match(/vimeo\.com\/(?:video\/)?(\d+)/)
    if (m) id = m[1]
    else if (/^\d+$/.test(raw)) id = raw
    if (!id) return null
    return `https://player.vimeo.com/video/${id}?muted=1&autoplay=1&loop=1&background=0&autopause=0`
  }

  const moverCarr = (dir) => {
    if (!carrRef.current) return
    const track = carrRef.current.querySelector('[data-carr-track]')
    const slides = carrRef.current.querySelectorAll('[data-carr-slide]')
    if (!slides.length) return
    const slideW = slides[0].offsetWidth + 16
    const visible = Math.max(1, Math.floor(carrRef.current.offsetWidth / slideW))
    const maxIdx = Math.max(0, slides.length - visible)
    const next = Math.min(Math.max(carrPos + dir, 0), maxIdx)
    setCarrPos(next)
    if (track) track.style.transform = `translateX(-${next * slideW}px)`
  }

  const sinInversion = !inversion.total || inversion.total === 0

  // ── Proyección al cierre del mes ────────────────────────────────────
  const hoy = new Date()
  const mesRef = mesFilter || `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}`
  const [anoP, mesP] = mesRef.split('-').map(Number)
  const diasEnMes = new Date(anoP, mesP, 0).getDate()
  const esMesActual = !mesFilter || mesFilter === `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,'0')}`
  const diaActual   = esMesActual ? hoy.getDate() : diasEnMes
  const pctMes      = Math.round((diaActual / diasEnMes) * 100)
  const proyLeads     = diaActual > 0 ? Math.round((+resumen.total_leads    / diaActual) * diasEnMes) : 0
  const proyInscritos = diaActual > 0 ? Math.round((+resumen.total_inscritos / diaActual) * diasEnMes) : 0
  const metaLeads     = +resumen.meta_leads || 0
  const leadsPorDia   = diaActual > 0 ? Math.round(+resumen.total_leads / diaActual) : 0
  const inscritosPorDia = diaActual > 0 ? (+resumen.total_inscritos / diaActual).toFixed(1) : '0'

  // ── Stats por plataforma (FB vs TT) ─────────────────────────────────
  const statsPlatformas = ['facebook', 'tiktok'].map(plat => {
    const ps   = piezas.filter(p => p.pagina === plat)
    const inv  = ps.reduce((s, p) => s + (+p.inversion     || 0), 0)
    const lds  = ps.reduce((s, p) => s + (+p.cantidad_leads || 0), 0)
    const impr = ps.reduce((s, p) => s + (+p.impresiones   || 0), 0)
    const alc  = ps.reduce((s, p) => s + (+p.alcance       || 0), 0)
    const psCtr    = ps.filter(p => (+p.ctr) > 0)
    const ctrProm  = psCtr.length ? psCtr.reduce((s, p) => s + (+p.ctr || 0), 0) / psCtr.length : 0
    const cpa      = lds > 0 ? inv / lds : 0
    const fPlat    = fuentes.filter(f => (f.nombre_fuente || '').toLowerCase().includes(plat))
    const ins      = fPlat.reduce((s, f) => s + (+f.inscritos      || 0), 0)
    const rev      = fPlat.reduce((s, f) => s + (+f.revenue_fuente || 0), 0)
    const escalar  = ps.filter(p => p.accion === 'escalar').length
    return { plat, inv, lds, impr, alc, ctrProm, cpa, ins, rev, escalar, count: ps.length }
  })

  return (
    <Layout>
      <HeaderContent title="Reporte de Marketing - KPIs" icon="group" />
      <Breadcrumb link={[
        { url: '', nombre: 'Marketing' },
        { url: '', nombre: 'Reportes' },
        { url: '', nombre: 'KPIs' },
      ]} />

      {/* ── Selector ─────────────────────────────────────────────────────── */}
      <div className="box p-3 mb-3">
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
          <div className="d-flex align-items-center mr-2">
            <FontAwesomeIcon icon={faBullhorn} style={{ color: C_B, marginRight: 8, fontSize: 16 }} />
            <span style={{ fontWeight: 700, fontSize: 15 }}>Programa</span>
          </div>
          {loadingProy
            ? <div className="spinner-border text-primary spinner-border-sm" />
            : <ProgramCombobox proyectos={proyectos} pidSel={pidSel} onChange={setPidSel} />
          }
          <div className="d-flex align-items-center ml-auto" style={{ gap: 8 }}>
            <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#6c757d', fontSize: 13 }} />
            <label style={{ fontSize: 13, fontWeight: 600, color: '#555', margin: 0 }}>Mes</label>
            <input type="month" value={mesFilter} onChange={e => setMesFilter(e.target.value)}
              className="form-control form-control-sm" style={{ width: 160 }} />
            {mesFilter && (
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setMesFilter('')} title="Limpiar">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
            <button className="btn btn-sm btn-outline-secondary" onClick={() => cargarData(pidSel, mesFilter)} title="Actualizar reporte">
              <FontAwesomeIcon icon={faSyncAlt} />
            </button>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={runSync}
              disabled={syncing || !pidSel}
              title="Sincronizar datos de Facebook y TikTok"
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={syncing} className="mr-1" />
              {syncing ? 'Sincronizando...' : 'Sincronizar'}
            </button>
            <button className="btn btn-sm btn-outline-secondary" title="Exportar CSV" onClick={exportarCSV}>
              <FontAwesomeIcon icon={faFileDownload} />
            </button>
          </div>
        </div>
      </div>

      {syncMsg && (
        <div className={`alert alert-${syncMsg.ok ? 'success' : 'danger'} py-2 mb-2`} style={{ fontSize: 13 }}>
          <FontAwesomeIcon icon={syncMsg.ok ? faCheckCircle : faExclamationTriangle} className="mr-2" />
          {syncMsg.text}
        </div>
      )}
      <SpinnerLoading loading={loading}>
        <ErrorBoundary key={pidSel}>
        {data && (<>

          {/* ── Aviso sync desactualizado ─────────────────────────────────── */}
          {sync.stale && (
            <div className="alert d-flex align-items-start mb-3" style={{ gap: 10, fontSize: 13, backgroundColor: '#fff8e1', border: '1px solid #ffc107', color: '#6d4c00', borderRadius: 6 }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#e6a800', flexShrink: 0, marginTop: 2 }} />
              <span>
                <strong>Datos de engagement desactualizados</strong> — El sync de Facebook/TikTok no ha corrido en más de 48 h.
                {sync.ultimo && <span style={{ marginLeft: 6, opacity: 0.75 }}>Último sync: {sync.ultimo.slice(0, 16)}</span>}
                <span style={{ display: 'block', marginTop: 3, fontSize: 11, opacity: 0.8 }}>
                  CTR, retención y score pueden no reflejar los valores actuales. Ejecuta <code>/admin/ad-sync/facebook</code> y <code>/admin/ad-sync/tiktok</code>.
                </span>
              </span>
            </div>
          )}

          {/* ── Objetivo general de campaña (criterio 2 HU-16) ──────────────── */}
          {objetivoGeneral && objetivoGeneral.meta_matriculas > 0 && (
            <div className="box mb-3">
              <div className="box-body" style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>
                      Objetivo general de campaña
                      {objetivoGeneral.meta_estimada && <span style={{ fontSize: 10, fontWeight: 500, color: '#aaa', marginLeft: 6 }}>(meta estimada: leads × % respuesta × % conversión)</span>}
                    </div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>
                      {objetivoGeneral.fecha_inicio} → {objetivoGeneral.fecha_fin || 'sin fecha fin'}
                      {objetivoGeneral.dias_habiles_total != null && (
                        <span> · día hábil {objetivoGeneral.dias_transcurridos}/{objetivoGeneral.dias_habiles_total} ({objetivoGeneral.pct_tiempo}% del tiempo)</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: colorRitmoObj(objetivoGeneral.pct_vs_ritmo), display: 'inline-block' }} />
                    <span style={{ fontSize: 20, fontWeight: 800, color: '#1e293b' }}>{fmt(objetivoGeneral.inscritos_actual)}</span>
                    <span style={{ fontSize: 13, color: '#888' }}>/ {fmt(objetivoGeneral.meta_matriculas)} matrículas</span>
                    {objetivoGeneral.pct_logrado != null && <span style={{ fontSize: 12, color: '#aaa' }}>({objetivoGeneral.pct_logrado}%)</span>}
                  </div>
                </div>
                {objetivoGeneral.dias_habiles_total != null && (
                  <div style={{ marginTop: 10, height: 6, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, objetivoGeneral.pct_tiempo)}%`, height: '100%', background: '#ccc' }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── BLOQUE 1: KPIs de embudo ─────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `${C_B}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FontAwesomeIcon icon={faUsers} style={{ color: C_B, fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>Embudo de captación</div>
              {variacion.mes_ref && <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>comparado con {mesLbl(variacion.mes_ref)}</div>}
            </div>
          </div>
          <div className="row mb-3">
            {[
              { label: 'Calidad del Lead', sub: 'Tratos / Leads × 100', valor: `${calidad}%`, meta: `Meta ≥${META_CALIDAD}%`, color: semVerde(calidad, META_CALIDAD), icon: faHandshake, detalle: `${fmt(resumen.total_tratos)} tratos de ${fmt(resumen.total_leads)} leads`, variacion: variacion.calidad },
              { label: 'Conversión',       sub: 'Inscritos / Leads × 100', valor: `${conv}%`, meta: `Meta ≥${META_CONV}%`,  color: semVerde(conv, META_CONV),    icon: faUserCheck, detalle: `${fmt(resumen.total_inscritos)} inscritos`, variacion: variacion.conversion },
              { label: 'Total Leads',      sub: 'Período',          valor: fmt(resumen.total_leads),    meta: null, color: C_B,  icon: faUsers,      variacion: variacion.leads     },
              { label: 'Contactados',      sub: 'Leads con respuesta', valor: fmt(resumen.total_contactados), meta: null, color: '#20c997', icon: faPhoneVolume },
              { label: 'Tratos',           sub: 'En embudo',        valor: fmt(resumen.total_tratos),   meta: null, color: C_A,  icon: faBullhorn,   variacion: variacion.tratos    },
              { label: 'Reservas',         sub: 'Confirmar depósito', valor: fmt(resumen.total_reservas), meta: null, color: '#6f42c1', icon: faLock },
              { label: 'Inscritos',        sub: 'Cerrados (fase 5)',valor: fmt(resumen.total_inscritos),meta: null, color: C_V,  icon: faCheckCircle,variacion: variacion.inscritos },
              { label: 'Mejor Conv.',      sub: 'Fuente por conversión', valor: resumen.mejor_fuente_conv || '—', meta: null, color: C_P,       icon: faStar,    small: true },
              { label: 'Mejor ROI',        sub: 'Fuente por ROI',        valor: resumen.mejor_fuente_roi  || '—', meta: null, color: '#17a2b8', icon: faTrophy,  small: true },
            ].map((k, i) => (
              <div key={i} className="col-6 col-md-4 col-lg mb-3" style={{ minWidth: 130 }}>
                <KpiCard {...k} />
              </div>
            ))}
          </div>

          {/* ── Proyección al cierre del mes ────────────────────────────── */}
          {resumen.total_leads > 0 && esMesActual && diaActual < diasEnMes && (
            <div className="box mb-3">
              <div className="box-body" style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: `${C_B}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FontAwesomeIcon icon={faArrowUp} style={{ color: C_B, fontSize: 13 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>Proyección al cierre del mes</div>
                    <div style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>Día {diaActual} de {diasEnMes} — {pctMes}% del mes transcurrido</div>
                  </div>
                </div>

                {/* Barra de progreso del mes */}
                <div style={{ height: 6, background: '#e9ecef', borderRadius: 3, overflow: 'hidden', marginBottom: 14, position: 'relative' }}>
                  <div style={{ height: '100%', width: `${pctMes}%`, background: `linear-gradient(90deg, ${C_B}, ${C_P})`, borderRadius: 3, transition: 'width .4s ease' }} />
                </div>

                <div className="row">
                  {/* Leads proyectados */}
                  <div className="col-md-6 mb-3 mb-md-0">
                    <div style={{ background: `${C_B}07`, border: `1px solid ${C_B}22`, borderRadius: 8, padding: '12px 16px' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C_B, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>Leads al cierre</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 30, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>~{fmt(proyLeads)}</span>
                        {metaLeads > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: (proyLeads >= metaLeads ? C_V : C_R) + '18', color: proyLeads >= metaLeads ? C_V : C_R }}>
                            {proyLeads >= metaLeads ? '✓' : '↓'} meta {fmt(metaLeads)}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                        Ritmo actual: <strong>{fmt(leadsPorDia)}</strong> leads / día
                      </div>
                    </div>
                  </div>

                  {/* Inscritos proyectados */}
                  <div className="col-md-6">
                    <div style={{ background: `${C_V}07`, border: `1px solid ${C_V}22`, borderRadius: 8, padding: '12px 16px' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C_V, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>Inscritos al cierre</div>
                      <div style={{ fontSize: 30, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>~{proyInscritos}</div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                        Ritmo actual: <strong>{inscritosPorDia}</strong> inscritos / día
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mensaje resumen */}
                <div style={{ marginTop: 12, padding: '8px 14px', background: '#f8f9fa', borderRadius: 6, fontSize: 12, color: '#555' }}>
                  💡 Al ritmo actual cerrarás <strong>{mesLbl(mesRef)}</strong> con <strong>~{fmt(proyLeads)} leads</strong> y <strong>~{proyInscritos} inscritos</strong>.
                  {metaLeads > 0 && proyLeads < metaLeads
                    ? <span style={{ color: C_R }}> Faltan <strong>{fmt(metaLeads - +resumen.total_leads)} leads</strong> en {diasEnMes - diaActual} días para la meta.</span>
                    : metaLeads > 0
                    ? <span style={{ color: C_V }}> 🎯 Superarás la meta de {fmt(metaLeads)} leads.</span>
                    : null}
                </div>
              </div>
            </div>
          )}

          {/* ── Velocidad del pipeline ───────────────────────────────────── */}
          {(velocidad.dias_lead_trato != null || velocidad.dias_trato_inscrito != null) && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#17a2b818', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FontAwesomeIcon icon={faClock} style={{ color: '#17a2b8', fontSize: 13 }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>Velocidad del pipeline</div>
                  {variacion.mes_ref && <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>vs {variacion.mes_ref}</div>}
                </div>
              </div>
              <div className="row mb-3">
                {velocidad.dias_lead_trato != null && (
                  <div className="col-md-6 mb-3 mb-md-0">
                    <VelocidadCard
                      titulo="Lead → Primer contacto (trato)"
                      dias={velocidad.dias_lead_trato}
                      umbrales={[1, 3]}
                      labels={['Rápido', 'Aceptable', 'Lento']}
                      tip="Cada día extra reduce la probabilidad de conversión. Meta: contactar en menos de 1 día."
                    />
                  </div>
                )}
                {velocidad.dias_trato_inscrito != null && (
                  <div className="col-md-6">
                    <VelocidadCard
                      titulo="Trato → Inscrito (cierre de venta)"
                      dias={velocidad.dias_trato_inscrito}
                      umbrales={[7, 21]}
                      labels={['Rápido', 'Normal', 'Largo']}
                      tip="Ciclo de venta promedio desde el primer contacto hasta el pago. Meta: cerrar en menos de 7 días."
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── BLOQUE 2: KPIs de inversión ──────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `${C_P}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: C_P, fontSize: 13 }} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>Inversión y rentabilidad</div>
          </div>
          {sinInversion ? (
            <div className="alert alert-light border mb-3 d-flex align-items-center" style={{ gap: 10, fontSize: 13 }}>
              <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#aaa', fontSize: 18 }} />
              <span>No hay anuncios registrados en la campaña activa de este programa. Los KPIs de inversión, CPA y ROI aparecerán cuando se carguen anuncios desde <strong>Campañas → Anuncios</strong>.</span>
            </div>
          ) : (
            <div className="row mb-3">
              {[
                { label: 'Inversión Total',   sub: `${inversion.cantidad_anuncios} anuncio(s)`, valor: fmtS(inversion.total),    meta: null,               color: C_P,   icon: faMoneyBillWave },
                { label: 'CPA en S/.',        sub: 'Inversión / inscritos',                     valor: fmtS(inversion.cpa_soles), meta: null,               color: semCPApct(inversion.cpa_pct), icon: faBullseye, small: true },
                { label: 'CPA %',             sub: '% del precio del programa',                 valor: inversion.cpa_pct > 0 ? `${inversion.cpa_pct}%` : '—', meta: `Meta ≤${META_CPA_PCT}%`, color: semCPApct(inversion.cpa_pct), icon: faPercentage },
                { label: 'ROI',               sub: '(Revenue − Inversión) / Inversión',         valor: inversion.roi_pct !== 0 ? `${inversion.roi_pct}%` : '—', meta: null, color: semROI(inversion.roi_pct), icon: faTrophy },
                { label: 'Revenue',           sub: 'Σ importe inscritos',                       valor: fmtS(resumen.revenue),    meta: null,               color: C_V,   icon: faChartPie, small: true },
                { label: 'Precio Promedio',   sub: 'Promedio por inscrito',                     valor: fmtS(resumen.precio_producto),meta: null,               color: '#17a2b8', icon: faArrowDown, small: true },
              ].map((k, i) => (
                <div key={i} className="col-6 col-md-4 col-lg-2 mb-3">
                  <KpiCard {...k} />
                </div>
              ))}
            </div>
          )}

          {/* ── BLOQUE 3: Eficiencia del presupuesto (criterio 4) ────────── */}
          {(eficiencia.pct_ppto_ejecutado > 0 || inversion.total > 0) && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#17a2b818', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FontAwesomeIcon icon={faChartPie} style={{ color: '#17a2b8', fontSize: 13 }} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>Eficiencia del presupuesto</div>
              </div>
              <div className="box mb-3">
                <div className="box-body" style={{ padding: '18px 20px' }}>
                  <div className="row">

                    {/* Presupuesto ejecutado vs avance de meta comercial (matrícula si está configurada, si no leads) */}
                    {eficiencia.pct_ppto_ejecutado > 0 && (
                      <div className="col-md-6 mb-3 mb-md-0">
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#434343' }}>
                              Presupuesto ejecutado vs {eficiencia.usa_matricula ? 'Matrícula lograda' : 'Leads logrados'}
                            </span>
                            <Pill color={eficiencia.estado === 'eficiente' ? C_V : eficiencia.estado === 'aceptable' ? C_A : C_R} size={11}>
                              {eficiencia.estado === 'eficiente' ? 'Eficiente' : eficiencia.estado === 'aceptable' ? 'Aceptable' : 'Riesgo'}
                            </Pill>
                          </div>
                          <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>
                            Presupuesto ejecutado: <strong style={{ color: eficiencia.ppto_sobre_80 ? C_R : '#434343' }}>{eficiencia.pct_ppto_ejecutado}%</strong>
                            {inversion.presupuesto > 0 && <span style={{ color: '#aaa' }}> ({fmtS(inversion.total)} / {fmtS(inversion.presupuesto)})</span>}
                            {eficiencia.ppto_sobre_80 && <span style={{ color: C_R, fontWeight: 700 }}> · supera el 80%</span>}
                          </div>
                          <div style={{ height: 10, backgroundColor: '#e9ecef', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                            <div style={{ height: '100%', width: `${Math.min(eficiencia.pct_ppto_ejecutado, 100)}%`, backgroundColor: eficiencia.ppto_sobre_80 ? C_R : C_P, borderRadius: 6, transition: 'width .4s ease' }} />
                          </div>
                          {eficiencia.usa_matricula ? (
                            <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>
                              Matrícula lograda: <strong>{eficiencia.pct_matricula_lograda}%</strong>
                              <span style={{ color: '#aaa' }}> ({fmt(resumen.total_inscritos)} / {fmt(eficiencia.meta_matriculas)})</span>
                            </div>
                          ) : (
                            <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>
                              Leads logrados: <strong>{eficiencia.pct_leads_logrados}%</strong>
                              {resumen.meta_leads > 0 && <span style={{ color: '#aaa' }}> ({fmt(resumen.total_leads)} / {fmt(resumen.meta_leads)})</span>}
                            </div>
                          )}
                          <div style={{ height: 10, backgroundColor: '#e9ecef', borderRadius: 6, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(eficiencia.usa_matricula ? eficiencia.pct_matricula_lograda : eficiencia.pct_leads_logrados, 100)}%`, backgroundColor: eficiencia.estado === 'eficiente' ? C_V : eficiencia.estado === 'aceptable' ? C_A : C_R, borderRadius: 6, transition: 'width .4s ease' }} />
                          </div>
                          {eficiencia.ratio > 0 && (
                            <div className="mt-2" style={{ fontSize: 11, color: '#888' }}>
                              Ratio eficiencia: <strong style={{ color: eficiencia.ratio >= 1 ? C_V : eficiencia.ratio >= 0.7 ? C_A : C_R }}>{eficiencia.ratio}x</strong>
                              &nbsp;{eficiencia.ratio >= 1 ? `— ${eficiencia.usa_matricula ? 'la matrícula avanza' : 'los leads avanzan'} al ritmo del gasto` : `— el gasto avanza más rápido que ${eficiencia.usa_matricula ? 'la matrícula' : 'los leads'}`}
                            </div>
                          )}
                          {!eficiencia.usa_matricula && (
                            <div className="mt-2" style={{ fontSize: 10, color: '#aaa', fontStyle: 'italic' }}>
                              Configura el "Objetivo total de matrículas" en KPIs Ventas → Configurar campaña para comparar contra la meta comercial real.
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Eficiencia de CPA vs objetivo */}
                    {inversion.total > 0 && inversion.cpa_soles > 0 && eficiencia.cpa_meta > 0 && (
                      <div className={eficiencia.pct_ppto_ejecutado > 0 ? 'col-md-6' : 'col-12'}>
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#434343' }}>
                              Eficiencia de CPA (real vs objetivo)
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 800, color: eficiencia.eficiencia_pct >= 100 ? C_V : eficiencia.eficiencia_pct >= 60 ? C_A : C_R }}>
                              {eficiencia.eficiencia_pct}%
                            </span>
                          </div>
                          <div style={{ height: 12, backgroundColor: '#e9ecef', borderRadius: 6, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%',
                              width: `${Math.min(eficiencia.eficiencia_pct, 100)}%`,
                              backgroundColor: eficiencia.eficiencia_pct >= 100 ? C_V : eficiencia.eficiencia_pct >= 60 ? C_A : C_R,
                              borderRadius: 6,
                              transition: 'width .4s ease',
                            }} />
                          </div>
                          <div className="d-flex justify-content-between mt-1">
                            <span style={{ fontSize: 11, color: '#888' }}>CPA real: {fmtS(inversion.cpa_soles)}</span>
                            <span style={{ fontSize: 11, color: '#888' }}>
                              CPA objetivo ({inversion.cpa_meta_pct > 0 ? `${inversion.cpa_meta_pct}%` : '5%'}): {fmtS(eficiencia.cpa_meta)}
                            </span>
                          </div>
                          <div className="mt-2">
                            {eficiencia.eficiencia_pct >= 100
                              ? <Pill color={C_V} size={11}>✓ CPA dentro del objetivo</Pill>
                              : <Pill color={C_R} size={11}>CPA supera el objetivo en {fmtS(+(inversion.cpa_soles - eficiencia.cpa_meta).toFixed(2))}</Pill>
                            }
                          </div>
                        </div>
                      </div>
                    )}

                    {eficiencia.pct_ppto_ejecutado === 0 && !(inversion.total > 0 && inversion.cpa_soles > 0) && (
                      <div className="col-12 text-center py-2" style={{ color: '#aaa', fontSize: 13 }}>
                        Configura el presupuesto en <strong>Campañas → Metas</strong> para ver la eficiencia.
                      </div>
                    )}

                    {/* CPL — costo por lead a nivel de campaña (criterios 6-7 HU-16) */}
                    {inversion.cpl_soles > 0 && (
                      <div className="col-12 mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#434343' }}>
                            CPL — Costo por Lead (campaña)
                          </span>
                          <Pill color={inversion.cpl_sobre_limite ? C_R : C_V} size={11}>
                            {inversion.cpl_sobre_limite ? 'Supera límite' : 'Dentro de meta'}
                          </Pill>
                        </div>
                        <div className="d-flex justify-content-between" style={{ fontSize: 11, color: '#888' }}>
                          <span>CPL real: <strong style={{ color: inversion.cpl_sobre_limite ? C_R : '#434343' }}>{fmtS(inversion.cpl_soles)}</strong></span>
                          <span>Límite: {fmtS(inversion.cpl_meta_soles)}</span>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Diagnóstico automático ────────────────────────────────────── */}
          {diagnostico && (diagnostico.alertas?.length > 0 || diagnostico.acciones?.length > 0) && (
            <div className="box mb-3" style={{ borderLeft: `4px solid ${diagnostico.estado === 'ok' ? C_V : diagnostico.estado === 'advertencia' ? C_A : C_R}` }}>
              <div className="box-header with-border" style={{ padding: '12px 18px' }}>
                <h5 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#434343' }}>
                  <FontAwesomeIcon icon={diagnostico.estado === 'ok' ? faCheckCircle : faExclamationTriangle} className="mr-2"
                    style={{ color: diagnostico.estado === 'ok' ? C_V : diagnostico.estado === 'advertencia' ? C_A : C_R }} />
                  Diagnóstico automático
                  <Pill color={diagnostico.estado === 'ok' ? C_V : diagnostico.estado === 'advertencia' ? C_A : C_R} size={11}>
                    &nbsp;{diagnostico.estado === 'ok' ? 'Sin alertas' : diagnostico.estado === 'advertencia' ? 'Advertencia' : 'Crítico'}
                  </Pill>
                </h5>
              </div>
              <div className="box-body" style={{ padding: '14px 18px' }}>
                {diagnostico.alertas?.map((a, i) => (
                  <div key={i} className="d-flex align-items-start mb-2" style={{ gap: 8 }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: C_R, marginTop: 2, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: '#555' }}>{a}</span>
                  </div>
                ))}
                {diagnostico.acciones?.length > 0 && (
                  <div style={{ marginTop: diagnostico.alertas?.length > 0 ? 10 : 0 }}>
                    {diagnostico.alertas?.length > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Acciones recomendadas</div>}
                    {diagnostico.acciones.map((a, i) => (
                      <div key={i} className="d-flex align-items-start mb-1" style={{ gap: 8 }}>
                        <span style={{ color: C_B, fontWeight: 700, flexShrink: 0 }}>→</span>
                        <span style={{ fontSize: 13, color: '#555' }}>{a}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Diagnóstico ejecutivo de cierre (criterio 28 HU-16) ─────────── */}
          {diagnostico && (
            <div className="box mb-3">
              <div className="box-header with-border" style={{ padding: '12px 18px' }}>
                <h5 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#434343' }}>
                  <FontAwesomeIcon icon={faChartPie} className="mr-2 text-primary" />
                  Diagnóstico ejecutivo de cierre
                </h5>
              </div>
              <div className="box-body" style={{ padding: '14px 18px' }}>
                <div className="row">
                  {[
                    { label: 'Qué funciona',            items: diagnostico.que_funciona,        color: C_V },
                    { label: 'Escalar',                 items: diagnostico.escalar,             color: C_V },
                    { label: 'Mantener',                items: diagnostico.mantener,            color: '#17a2b8' },
                    { label: 'Optimizar',                items: diagnostico.optimizar,           color: C_A },
                    { label: 'Pausar',                  items: diagnostico.pausar,               color: C_R },
                    { label: 'Marketing debe hacer hoy', items: diagnostico.hoy_marketing,        color: C_B },
                    { label: 'Operaciones debe revisar', items: diagnostico.revisar_operaciones,  color: C_P },
                  ].map(({ label, items, color }) => (
                    <div key={label} className="col-md-6 mb-3">
                      <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
                      {items?.length > 0 ? (
                        items.map((it, i) => (
                          <div key={i} className="d-flex align-items-start mb-1" style={{ gap: 6 }}>
                            <span style={{ color, fontWeight: 700, flexShrink: 0 }}>•</span>
                            <span style={{ fontSize: 12, color: '#555' }}>{it}</span>
                          </div>
                        ))
                      ) : (
                        <span style={{ fontSize: 12, color: '#ccc' }}>— sin datos —</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Gráfico histórico (criterio 22: diario/semanal/mensual) ─────── */}
          <div className="box mb-3">
            <div className="box-header with-border d-flex align-items-center justify-content-between flex-wrap" style={{ padding: '12px 18px', gap: 10 }}>
              <h5 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#434343' }}>
                <FontAwesomeIcon icon={faChartLine} className="mr-2 text-primary" />
                Evolución histórica — Leads · Tratos · Inscritos{periodoHist === 'mensual' ? ' · Revenue' : ''}
              </h5>
              <div style={{ display: 'flex', background: '#f1f3f5', borderRadius: 8, padding: 2 }}>
                {[['diario','Diario'],['semanal','Semanal'],['mensual','Mensual']].map(([v, lbl]) => (
                  <button key={v} onClick={() => setPeriodoHist(v)}
                    style={{ border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', background: periodoHist === v ? '#fff' : 'transparent', color: periodoHist === v ? '#333' : '#999', boxShadow: periodoHist === v ? '0 1px 3px rgba(0,0,0,.15)' : 'none' }}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <div className="box-body" style={{ padding: '8px 16px 12px' }}>
              {periodoHist === 'diario'  && <ChartHistorico data={historicoDiario}  xField="periodo" labelFn={diaLbl} showRevenue={false} />}
              {periodoHist === 'semanal' && <ChartHistorico data={historicoSemanal} xField="periodo" labelFn={diaLbl} showRevenue={false} />}
              {periodoHist === 'mensual' && <ChartHistorico data={historico} />}
            </div>
          </div>

          {/* ── Histórico por fuente (criterio 22) ──────────────────────────── */}
          {historicoFuente.length > 0 && (
            <div className="box mb-3">
              <div className="box-header with-border" style={{ padding: '12px 18px' }}>
                <h5 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#434343' }}>
                  <FontAwesomeIcon icon={faChartLine} className="mr-2 text-primary" />
                  Evolución mensual por fuente
                </h5>
              </div>
              <div className="box-body table-responsive" style={{ padding: 0 }}>
                <table className="table mb-0" style={{ fontSize: 12 }}>
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>
                      <th style={{ padding: '8px 12px' }}>Mes</th>
                      <th style={{ padding: '8px 12px' }}>Fuente</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center' }}>Leads</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center' }}>Tratos</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center' }}>Inscritos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicoFuente.map((h, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '6px 12px' }}>{mesLbl(h.mes)}</td>
                        <td style={{ padding: '6px 12px' }}>{h.fuente || 'Sin fuente'}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'center' }}>{fmt(h.leads)}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'center' }}>{fmt(h.tratos)}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'center' }}>{fmt(h.inscritos)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Ranking de piezas / anuncios ──────────────────────────────── */}
          {piezas.length > 0 && (
            <div className="box mb-3">
              <div className="box-header with-border d-flex align-items-center justify-content-between" style={{ padding: '12px 18px' }}>
                <h5 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#434343' }}>
                  <FontAwesomeIcon icon={faBullseye} className="mr-2" style={{ color: C_P }} />
                  Ranking de piezas publicitarias
                </h5>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 6 }}>
                  {['escalar','mantener','revisar','pausar'].map(a => {
                    const cnt = piezas.filter(p => p.accion === a).length
                    if (!cnt) return null
                    const cfg = ACCION_CFG[a]
                    return <span key={a} style={{ fontSize: 10, fontWeight: 700, color: cfg.color, background: cfg.color+'18', padding: '2px 9px', borderRadius: 20 }}>{cnt} {a}</span>
                  })}
                  <div style={{ display: 'flex', background: '#f0f0f0', borderRadius: 8, padding: 3, gap: 2 }}>
                    <button onClick={() => setVistaCarrusel(false)}
                      style={{ border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', background: !vistaCarrusel ? '#fff' : 'transparent', color: !vistaCarrusel ? '#333' : '#999', boxShadow: !vistaCarrusel ? '0 1px 3px rgba(0,0,0,.15)' : 'none' }}>
                      ☰ Tabla
                    </button>
                    <button onClick={() => { setVistaCarrusel(true); setCarrPos(0) }}
                      style={{ border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', background: vistaCarrusel ? '#fff' : 'transparent', color: vistaCarrusel ? '#333' : '#999', boxShadow: vistaCarrusel ? '0 1px 3px rgba(0,0,0,.15)' : 'none' }}>
                      ▶ Carrusel
                    </button>
                  </div>
                </div>
              </div>
              <div className="box-body p-0">

              {/* ── Podio Top 3 ── */}
              {(topScore || topCTR || topLeads) && (
                <div style={{ display: 'flex', gap: 10, padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                  {[
                    { label: 'Mayor Score', icon: '🏆', p: topScore, val: topScore ? `${topScore.score} pts`             : null, color: C_V  },
                    { label: 'Mayor CTR',   icon: '⚡', p: topCTR,   val: topCTR   ? `${topCTR.ctr}% CTR`               : null, color: C_B  },
                    { label: 'Más Leads',   icon: '🎯', p: topLeads, val: topLeads ? `${fmt(topLeads.cantidad_leads)} leads` : null, color: C_A  },
                  ].filter(x => x.p).map(({ label, icon, p, val, color }) => (
                    <div key={label} style={{ flex: 1, minWidth: 0, borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,.07)', border: '1px solid #ebebeb' }}>
                      <div style={{ height: 3, background: `linear-gradient(90deg, ${color}, ${color}99)` }} />
                      <div style={{ padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center' }}>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{icon}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: .6, marginBottom: 2 }}>{label}</div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.nombre_anuncio}>{p.nombre_anuncio}</div>
                          <div style={{ fontSize: 15, fontWeight: 800, color, lineHeight: 1.2, marginTop: 1 }}>{val}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Chips de filtro ── */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#999', marginRight: 2 }}>
                  <FontAwesomeIcon icon={faFilter} style={{ marginRight: 4 }} />Filtrar:
                </span>
                {[['todos','Todas',C_B],['facebook','Facebook',C_B],['tiktok','TikTok',C_B]].map(([v,lbl,ac]) => (
                  <button key={v} onClick={() => setFiltroPlat(v)} style={{ padding: '3px 11px', borderRadius: 20, border: `1.5px solid ${filtroPlat===v ? ac : '#e0e0e0'}`, background: filtroPlat===v ? ac : '#fff', color: filtroPlat===v ? '#fff' : '#555', fontSize: 10, fontWeight: 600, cursor: 'pointer', transition: 'all .15s' }}>{lbl}</button>
                ))}
                <span style={{ width: 1, height: 14, background: '#e0e0e0', flexShrink: 0, margin: '0 2px' }} />
                {[['todos','Todo tipo',C_P],['video','Video',C_P],['imagen','Imagen',C_P]].map(([v,lbl,ac]) => (
                  <button key={v} onClick={() => setFiltroTipo(v)} style={{ padding: '3px 11px', borderRadius: 20, border: `1.5px solid ${filtroTipo===v ? ac : '#e0e0e0'}`, background: filtroTipo===v ? ac : '#fff', color: filtroTipo===v ? '#fff' : '#555', fontSize: 10, fontWeight: 600, cursor: 'pointer', transition: 'all .15s' }}>{lbl}</button>
                ))}
                <span style={{ width: 1, height: 14, background: '#e0e0e0', flexShrink: 0, margin: '0 2px' }} />
                {[['todos','Todas acciones'],['escalar','Escalar'],['mantener','Mantener'],['revisar','Revisar'],['pausar','Pausar']].map(([v,lbl]) => {
                  const ac = ACCION_CFG[v]?.color || '#555'
                  return <button key={v} onClick={() => setFiltroAccion(v)} style={{ padding: '3px 11px', borderRadius: 20, border: `1.5px solid ${filtroAccion===v ? ac : '#e0e0e0'}`, background: filtroAccion===v ? ac : '#fff', color: filtroAccion===v ? '#fff' : '#555', fontSize: 10, fontWeight: 600, cursor: 'pointer', transition: 'all .15s' }}>{lbl}</button>
                })}
                {fuentesPiezas.length > 1 && (
                  <>
                    <span style={{ width: 1, height: 14, background: '#e0e0e0', flexShrink: 0, margin: '0 2px' }} />
                    <select value={filtroFuente} onChange={e => setFiltroFuente(e.target.value)}
                      className="form-control form-control-sm" style={{ width: 'auto', fontSize: 10, height: 24, padding: '0 6px' }}>
                      <option value="todos">Toda fuente</option>
                      {fuentesPiezas.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </>
                )}
                {(filtroPlat !== 'todos' || filtroTipo !== 'todos' || filtroAccion !== 'todos' || filtroFuente !== 'todos') && (
                  <button onClick={() => { setFiltroPlat('todos'); setFiltroTipo('todos'); setFiltroAccion('todos'); setFiltroFuente('todos') }}
                    style={{ padding: '3px 9px', borderRadius: 20, border: '1.5px solid #dc3545', background: 'transparent', color: '#dc3545', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                    ✕ Limpiar
                  </button>
                )}
                <span style={{ marginLeft: 'auto', fontSize: 10, color: '#aaa', fontWeight: 600, flexShrink: 0 }}>
                  {filteredPiezas.length === piezas.length ? `${piezas.length} anuncios` : `${filteredPiezas.length} de ${piezas.length}`}
                </span>
              </div>

              {/* ── Vista carrusel ── */}
              {vistaCarrusel && (
                <div style={{ padding: '20px 24px 16px', position: 'relative' }} ref={carrRef}>
                  <button onClick={() => moverCarr(-1)}
                    style={{ position: 'absolute', left: 4, top: '45%', transform: 'translateY(-50%)', zIndex: 10, width: 34, height: 34, borderRadius: '50%', border: 'none', background: '#0F2044', color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,.25)' }}>‹</button>
                  <button onClick={() => moverCarr(1)}
                    style={{ position: 'absolute', right: 4, top: '45%', transform: 'translateY(-50%)', zIndex: 10, width: 34, height: 34, borderRadius: '50%', border: 'none', background: '#0F2044', color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,.25)' }}>›</button>

                  <div style={{ overflow: 'hidden', margin: '0 20px' }}>
                    <div data-carr-track style={{ display: 'flex', gap: 16, transition: 'transform .35s cubic-bezier(.22,1,.36,1)', willChange: 'transform' }}>
                      {filteredPiezas.map((p, i) => {
                        const isTT = p.pagina === 'tiktok'
                        const slideW = isTT ? 220 : 260
                        const slideH = (p.tipo_anuncio === 'video' || isTT) ? (isTT ? 391 : 260) : 200
                        const vimeoSrc = getVimeoSrc(p.embed_vimeo)
                        const ttSrc = isTT && p.tipo_anuncio === 'video' && p.id_externo && !vimeoSrc
                          ? `https://www.tiktok.com/embed/v2/${p.id_externo}?autoplay=1&muted=1` : null
                        const redColor = isTT ? '#010101' : '#1877F2'
                        const scoreColor = p.score === null ? '#ccc' : p.score >= 70 ? C_V : p.score >= 40 ? C_A : C_R
                        // Solo cargar media activa en slides cercanos al actual (evita lag con 77+ iframes)
                        const esVisible = Math.abs(i - carrPos) <= 2
                        return (
                          <div key={p.id_creative || i} data-carr-slide
                            style={{ minWidth: slideW, maxWidth: slideW, flexShrink: 0, borderRadius: 12, border: '1px solid #e9ecef', overflow: 'hidden', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
                            {/* Área de video/imagen */}
                            <div style={{ position: 'relative', background: '#0f172a', height: slideH, overflow: 'hidden', cursor: 'pointer' }} onClick={() => setModalPieza(p)}>
                              {esVisible && vimeoSrc ? (
                                <iframe src={vimeoSrc} width={slideW} height={slideH}
                                  style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', border: 0, pointerEvents: 'auto' }}
                                  allow="autoplay;fullscreen;picture-in-picture" title={p.nombre_anuncio} />
                              ) : esVisible && ttSrc ? (
                                <iframe src={ttSrc} width={slideW} height={slideH}
                                  style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', border: 0 }}
                                  allow="autoplay;fullscreen;picture-in-picture" title={p.nombre_anuncio} />
                              ) : esVisible && p.video_url ? (
                                <video src={p.video_url} autoPlay muted loop playsInline
                                  style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                              ) : p.thumbnail_url ? (
                                <img src={p.thumbnail_url} alt="" onError={e => e.target.style.display='none'}
                                  style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                              ) : (
                                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: '#334' }}>
                                  {p.tipo_anuncio === 'video' ? '🎬' : '🖼️'}
                                </div>
                              )}
                              {/* Badges */}
                              <div style={{ position: 'absolute', top: 8, right: 8, background: redColor, color: '#fff', borderRadius: 5, padding: '2px 7px', fontSize: 9, fontWeight: 700 }}>{(p.pagina || '').toUpperCase()}</div>
                              <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,.55)', color: '#fff', borderRadius: 5, padding: '2px 7px', fontSize: 9 }}>{(p.tipo_anuncio || '').toUpperCase()}</div>
                            </div>

                            {/* Info */}
                            <div style={{ padding: '10px 12px' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', marginBottom: 6, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.nombre_anuncio}>{p.nombre_anuncio}</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#64748b', marginBottom: 6 }}>
                                <span>Inv: <strong>S/ {Math.round(+p.inversion || 0)}</strong></span>
                                <span>Leads: <strong>{p.cantidad_leads}</strong></span>
                                {p.costo_lead > 0 && <span>CPA: <strong>S/ {p.costo_lead}</strong></span>}
                              </div>
                              {p.score !== null && (
                                <div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#94a3b8', marginBottom: 3 }}>
                                    <span>Score</span><span style={{ color: scoreColor, fontWeight: 700 }}>{p.score}</span>
                                  </div>
                                  <div style={{ height: 4, background: '#e9ecef', borderRadius: 2, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${p.score}%`, background: scoreColor }} />
                                  </div>
                                </div>
                              )}
                              <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: ACCION_CFG[p.accion]?.color || '#aaa' }}>{p.accion}</span>
                                {p.impresiones > 0 && <span style={{ fontSize: 9, color: '#94a3b8' }}>{fmt(p.impresiones)} impr</span>}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Dots */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14 }}>
                    {filteredPiezas.map((_, i) => (
                      <div key={i} onClick={() => { setCarrPos(i); if (carrRef.current) { const t = carrRef.current.querySelector('[data-carr-track]'); const s = carrRef.current.querySelectorAll('[data-carr-slide]'); if (t && s.length) t.style.transform = `translateX(-${i * (s[0].offsetWidth + 16)}px)` } }}
                        style={{ width: 7, height: 7, borderRadius: '50%', background: i === carrPos ? '#0F2044' : '#e2e8f0', cursor: 'pointer', transition: 'background .2s' }} />
                    ))}
                  </div>
                </div>
              )}

              {/* ── Vista tabla ── */}
              {!vistaCarrusel && <div className="table-responsive">
                  <table className="table mb-0" style={{ fontSize: 12 }}>
                    <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 2, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
                      <tr style={{ fontSize: 11, fontWeight: 700, color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'center', width: 36 }}>#</th>
                        <th style={{ padding: '10px 12px' }}>Pieza / Anuncio</th>
                        <th style={{ padding: '10px 12px' }}>Tipo</th>
                        <th style={{ padding: '10px 12px' }}>Plataforma</th>
                        <SortTh col="inversion"      sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ padding: '10px 12px', textAlign: 'right' }}>Inversión</SortTh>
                        <SortTh col="cantidad_leads" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ padding: '10px 12px', textAlign: 'center' }}>Leads</SortTh>
                        <SortTh col="costo_lead"     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ padding: '10px 12px', textAlign: 'right' }}>CPA Lead</SortTh>
                        <SortTh col="tratos_pieza"    sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ padding: '10px 12px', textAlign: 'center' }}>Tratos</SortTh>
                        <SortTh col="reservas_pieza"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ padding: '10px 12px', textAlign: 'center' }}>Reservas</SortTh>
                        <SortTh col="inscritos_pieza" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ padding: '10px 12px', textAlign: 'center' }}>Inscritos</SortTh>
                        <SortTh col="ctr"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ padding: '10px 12px', textAlign: 'center' }}>CTR</SortTh>
                        <SortTh col="score" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ padding: '10px 12px', textAlign: 'center' }}>Score</SortTh>
                        <th style={{ padding: '10px 12px', textAlign: 'center' }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPiezas.map((p, i) => {
                        const scoreColor = p.score === null ? '#ccc' : p.score >= 70 ? C_V : p.score >= 40 ? C_A : C_R
                        const sd = p.score_detalle
                        const scoreTooltip = sd
                          ? `CTR: ${sd.ctr}/${sd.ctr_max} · CPL: ${sd.cpl}/${sd.cpl_max}${sd.ret_max > 0 ? ` · Ret: ${sd.ret}/${sd.ret_max}` : ''}`
                          : null
                        return (
                          <tr key={p.id_creative || p.id_anuncio || i}
                            onMouseEnter={() => setHoveredRow(i)}
                            onMouseLeave={() => setHoveredRow(null)}
                            style={{ borderBottom: '1px solid #f0f0f0', background: hoveredRow === i ? '#f4f7ff' : 'transparent', transition: 'background .12s ease' }}>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              {i === 0
                                ? <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:'50%', background:'#FFD700', fontSize:10, fontWeight:800, color:'#7d5a00', boxShadow:'0 1px 3px rgba(200,150,0,.4)' }}>1</span>
                                : i === 1
                                ? <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:'50%', background:'#C0C0C0', fontSize:10, fontWeight:800, color:'#444', boxShadow:'0 1px 3px rgba(0,0,0,.2)' }}>2</span>
                                : i === 2
                                ? <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:'50%', background:'#CD7F32', fontSize:10, fontWeight:800, color:'#fff', boxShadow:'0 1px 3px rgba(150,80,0,.35)' }}>3</span>
                                : <span style={{ color:'#ccc', fontSize:11 }}>{i+1}</span>}
                            </td>
                            <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                {p.thumbnail_url && (
                                  <div style={{ position: 'relative', flexShrink: 0, cursor: 'pointer' }} onClick={() => setModalPieza(p)}>
                                    <img src={p.thumbnail_url} alt="" onError={e => e.target.parentNode.style.display='none'}
                                      style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 5, border: '1px solid #e9ecef', display: 'block' }} />
                                    {(p.tipo_anuncio === 'video' || p.video_url || p.embed_vimeo) && (
                                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.35)', borderRadius: 5 }}>
                                        <span style={{ color: '#fff', fontSize: 16, lineHeight: 1 }}>▶</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div style={{ flex: 1 }}>
                                  <div className="d-flex align-items-center" style={{ gap: 7 }}>
                                    <Dot color={ACCION_CFG[p.accion]?.color} />
                                    <span>{p.nombre_anuncio || '—'}</span>
                                  </div>
                                  {p.impresiones > 0 && (
                                    <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>
                                      {fmt(p.impresiones)} impr · {fmt(p.alcance)} alcance
                                      {p.retencion > 0 && <span> · ret. {p.retencion}%</span>}
                                      {p.frecuencia > 0 && (
                                        <span style={{ color: p.frecuencia_alerta ? C_R : '#aaa' }}>
                                          {' '}· frec. <strong>{p.frecuencia}</strong>
                                          {p.frecuencia_alerta && ' ⚠'}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {(p.quality_ranking || p.engagement_rate_ranking || p.conversion_rate_ranking) && (
                                    <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }}>
                                      {[
                                        [p.quality_ranking,          'Calidad'],
                                        [p.engagement_rate_ranking,  'Engagement'],
                                        [p.conversion_rate_ranking,  'Conversión'],
                                      ].map(([val, title]) => {
                                        if (!val || val === 'UNKNOWN') return null
                                        const color = val === 'ABOVE_AVERAGE' ? '#2a9d8f' : val === 'AVERAGE' ? '#bbb' : val.includes('_10') ? '#e76f51' : '#c0392b'
                                        const tip   = `${title}: ${val.replace(/_/g,' ').toLowerCase()}`
                                        return (
                                          <Tooltip key={title} text={tip}>
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                                          </Tooltip>
                                        )
                                      })}
                                    </div>
                                  )}
                                  {p.funnel && (
                                    <div style={{ display: 'flex', gap: 3, marginTop: 4, alignItems: 'center' }}>
                                      {[['25%', p.funnel.p25], ['50%', p.funnel.p50], ['75%', p.funnel.p75], ['100%', p.funnel.p100]].map(([lbl, pct]) => (
                                        <div key={lbl} title={`${lbl} completado: ${pct}% de quienes iniciaron`} style={{ textAlign: 'center' }}>
                                          <div style={{ width: 22, height: 28, backgroundColor: '#f0f0f0', borderRadius: 2, display: 'flex', alignItems: 'flex-end', overflow: 'hidden' }}>
                                            <div style={{ width: '100%', height: `${pct}%`, backgroundColor: pct >= 70 ? C_V : pct >= 40 ? C_A : C_R, transition: 'height .3s' }} />
                                          </div>
                                          <div style={{ fontSize: 8, color: '#aaa', marginTop: 1 }}>{lbl}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              {p.tipo_anuncio
                                ? <span style={{ fontSize:10, fontWeight:700, color: p.tipo_anuncio==='video' ? C_P : '#64748b', background: p.tipo_anuncio==='video' ? C_P+'18' : '#f1f5f9', padding:'2px 8px', borderRadius:20, display:'inline-flex', alignItems:'center', gap:3, whiteSpace:'nowrap' }}>
                                    {p.tipo_anuncio==='video' ? '▶' : '🖼'} {p.tipo_anuncio}
                                  </span>
                                : <span style={{color:'#ccc'}}>—</span>}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              {p.pagina
                                ? <span style={{ fontSize:10, fontWeight:700, color: p.pagina==='facebook' ? '#1877F2' : '#fff', background: p.pagina==='facebook' ? '#1877F218' : '#010101', padding:'2px 8px', borderRadius:20, display:'inline-flex', alignItems:'center', gap:3, whiteSpace:'nowrap' }}>
                                    {p.pagina==='facebook' ? 'f·' : '♪'} {p.pagina}
                                  </span>
                                : <span style={{color:'#ccc'}}>—</span>}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>{fmtS(p.inversion)}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700 }}>{fmt(p.cantidad_leads)}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                              {p.costo_lead > 0 ? (
                                <Pill color={p.costo_lead <= 50 ? C_V : p.costo_lead <= 100 ? C_A : C_R} size={11}>
                                  {fmtS(p.costo_lead)}
                                </Pill>
                              ) : <span style={{ color: '#ccc' }}>—</span>}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              {p.leads_crm > 0 ? fmt(p.tratos_pieza) : <span style={{ color: '#ccc' }}>—</span>}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              {p.leads_crm > 0 ? fmt(p.reservas_pieza) : <span style={{ color: '#ccc' }}>—</span>}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              {p.leads_crm > 0 ? fmt(p.inscritos_pieza) : <span style={{ color: '#ccc' }}>—</span>}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              {p.ctr > 0
                                ? <span style={{ fontWeight: 700, color: p.ctr >= 3 ? C_V : p.ctr >= 1 ? C_A : C_R }}>{p.ctr}%</span>
                                : <span style={{ color: '#ccc' }}>—</span>}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              {p.score !== null
                                ? (
                                  <Tooltip text={scoreTooltip}>
                                    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2, cursor: 'help' }}>
                                      <span style={{ fontWeight: 800, fontSize: 13, color: scoreColor }}>{p.score}</span>
                                      <div style={{ width: 32, height: 4, backgroundColor: '#e9ecef', borderRadius: 2, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${p.score}%`, backgroundColor: scoreColor, borderRadius: 2 }} />
                                      </div>
                                    </div>
                                  </Tooltip>
                                )
                                : <span style={{ color: '#ccc', fontSize: 11 }}>sin data</span>}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <AccionBadge accion={p.accion} />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #e9ecef' }}>
                      <tr>
                        <td colSpan={4} style={{ padding: '10px 12px', fontWeight: 700, fontSize: 12 }}>
                          TOTAL{filteredPiezas.length < piezas.length && <span style={{ fontWeight: 400, color: '#aaa', fontSize: 10, marginLeft: 6 }}>(filtrado)</span>}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>{fmtS(filteredPiezas.reduce((s, p) => s + p.inversion, 0))}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700 }}>{fmt(filteredPiezas.reduce((s, p) => s + p.cantidad_leads, 0))}</td>
                        <td colSpan={7} style={{ padding: '10px 12px', color: '#888', fontSize: 11 }}>
                          CPA prom: {fmtS(
                            filteredPiezas.filter(p => p.cantidad_leads > 0).length > 0
                              ? filteredPiezas.filter(p => p.cantidad_leads > 0).reduce((s, p) => s + p.costo_lead, 0) / filteredPiezas.filter(p => p.cantidad_leads > 0).length
                              : 0
                          )}
                          {filteredPiezas.some(p => p.score !== null) && (
                            <span style={{ marginLeft: 12 }}>
                              Score prom: <strong>{Math.round(filteredPiezas.filter(p => p.score !== null).reduce((s, p) => s + p.score, 0) / Math.max(1, filteredPiezas.filter(p => p.score !== null).length))}</strong>
                            </span>
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>}

              </div>
            </div>
          )}

          {/* ── Comparativa Facebook vs TikTok ───────────────────────────── */}
          {statsPlatformas.some(s => s.count > 0) && (
            <div className="box mb-3">
              <div className="box-header with-border" style={{ padding: '12px 18px' }}>
                <h5 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#434343' }}>
                  <FontAwesomeIcon icon={faChartPie} className="mr-2" style={{ color: C_B }} />
                  Facebook vs TikTok
                  <span style={{ fontSize: 11, fontWeight: 400, color: '#aaa', marginLeft: 8 }}>comparativa por plataforma</span>
                </h5>
              </div>
              <div className="box-body p-0">
                <div className="row" style={{ margin: 0 }}>
                  {statsPlatformas.filter(s => s.count > 0).map((s, idx) => {
                    const isFB   = s.plat === 'facebook'
                    const color  = isFB ? '#1877F2' : '#111'
                    const label  = isFB ? 'Facebook ADS' : 'TikTok ADS'
                    const totalInv = statsPlatformas.reduce((t, x) => t + x.inv, 0)
                    const pctInv   = totalInv > 0 ? Math.round((s.inv / totalInv) * 100) : 0
                    return (
                      <div key={s.plat} className="col-md-6" style={{ padding: '18px 22px', borderRight: idx === 0 && statsPlatformas.filter(x=>x.count>0).length > 1 ? '1px solid #f0f0f0' : 'none' }}>
                        {/* Header plataforma */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color, background: isFB ? '#1877F212' : '#f1f5f9', padding: '4px 12px', borderRadius: 20 }}>
                            {isFB ? 'f·' : '♪'} {label}
                          </span>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 10, color: '#aaa' }}>{s.count} anuncios · {pctInv}% inversión</div>
                            {s.escalar > 0 && <div style={{ fontSize: 10, color: C_V, fontWeight: 700 }}>{s.escalar} para escalar</div>}
                          </div>
                        </div>

                        {/* Barra de inversión */}
                        <div style={{ height: 5, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden', marginBottom: 14 }}>
                          <div style={{ height: '100%', width: `${pctInv}%`, background: color, borderRadius: 3 }} />
                        </div>

                        {/* Métricas en grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px 8px' }}>
                          {[
                            { lbl: 'Inversión',   val: fmtS(s.inv),                                       color: color },
                            { lbl: 'Leads',        val: fmt(s.lds),                                        color: C_B   },
                            { lbl: 'Inscritos',    val: s.ins > 0 ? fmt(s.ins) : '—',                        color: C_V   },
                            { lbl: 'CPA lead',     val: s.lds > 0 ? fmtS(+s.cpa.toFixed(2)) : '—',       color: s.cpa <= 50 ? C_V : s.cpa <= 100 ? C_A : C_R },
                            { lbl: 'CTR prom',     val: s.ctrProm > 0 ? `${s.ctrProm.toFixed(2)}%` : '—', color: s.ctrProm >= 2 ? C_V : s.ctrProm >= 1 ? C_A : '#aaa' },
                            { lbl: 'Impresiones',  val: s.impr > 0 ? fmt(s.impr) : '—',                   color: '#555' },
                          ].map(({ lbl, val, color: vc }) => (
                            <div key={lbl}>
                              <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 2 }}>{lbl}</div>
                              <div style={{ fontSize: 15, fontWeight: 800, color: vc, lineHeight: 1.1 }}>{val}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── Tabla de fuentes ──────────────────────────────────────────── */}
          <div className="box mb-3">
            <div className="box-header with-border d-flex align-items-center justify-content-between" style={{ padding: '12px 18px' }}>
              <h5 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#434343' }}>
                <FontAwesomeIcon icon={faFire} className="mr-2" style={{ color: C_A }} />
                Rendimiento por fuente
              </h5>
              <span style={{ fontSize: 12, color: '#888' }}>{fuentes.length} fuente(s)</span>
            </div>
            <div className="box-body p-0">
              {fuentes.length === 0 ? (
                <div className="text-center py-5" style={{ color: '#aaa' }}>
                  <FontAwesomeIcon icon={faFilter} style={{ fontSize: 36, display: 'block', margin: '0 auto 10px' }} />
                  <p style={{ fontSize: 14, fontWeight: 600 }}>Sin datos de fuentes para este programa</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0" style={{ fontSize: 13 }}>
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr style={{ fontSize: 12, fontWeight: 700, color: '#555', borderBottom: '2px solid #e9ecef' }}>
                        <th style={{ padding: '10px 16px' }}>#</th>
                        <th style={{ padding: '10px 16px' }}>Fuente</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center' }}>Leads</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center' }}>Tratos</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center' }}>Calidad%</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center' }}>Inscritos</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center' }}>Conv%</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right' }}>Revenue</th>
                        {!sinInversion && <th style={{ padding: '10px 16px', textAlign: 'right' }} title="Atribución proporcional por leads">CPA <span style={{ fontSize: 9, color: '#aaa' }}>*</span></th>}
                        {!sinInversion && <th style={{ padding: '10px 16px', textAlign: 'right' }} title="Atribución proporcional por leads">ROI <span style={{ fontSize: 9, color: '#aaa' }}>*</span></th>}
                        <th style={{ padding: '10px 16px', textAlign: 'center' }}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fuentes.map((f, i) => {
                        const roiColor = f.roi_fuente > 0 ? C_V : f.roi_fuente > -20 ? C_A : C_R
                        return (
                        <tr key={f.id_fuente} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '10px 16px', color: '#aaa', fontSize: 12 }}>{i + 1}</td>
                          <td style={{ padding: '10px 16px', fontWeight: 600 }}>
                            <div className="d-flex align-items-center" style={{ gap: 8 }}>
                              <Dot color={ESTADO_FUENTE[f.estado]?.color} />
                              {f.nombre_fuente}
                            </div>
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700 }}>{fmt(f.leads)}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>{fmt(f.tratos)}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <Pill color={semVerde(+f.pct_calidad, META_CALIDAD)}>{f.pct_calidad}%</Pill>
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700, color: C_V }}>{fmt(f.inscritos)}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <Pill color={semVerde(+f.pct_conv, META_CONV)}>{f.pct_conv}%</Pill>
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: C_P, fontWeight: 600 }}>
                            {f.revenue_fuente > 0 ? fmtS(f.revenue_fuente) : <span style={{ color: '#ccc' }}>—</span>}
                          </td>
                          {!sinInversion && (
                            <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                              {f.cpa_fuente > 0
                                ? <Pill color={semCPApct(inversion.cpa_pct)} size={11}>{fmtS(f.cpa_fuente)}</Pill>
                                : <span style={{ color: '#ccc' }}>—</span>}
                            </td>
                          )}
                          {!sinInversion && (
                            <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                              {f.inv_atribuida > 0
                                ? <Pill color={roiColor} size={11}>{f.roi_fuente}%</Pill>
                                : <span style={{ color: '#ccc' }}>—</span>}
                            </td>
                          )}
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <AccionBadge accion={f.estado} />
                          </td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  {!sinInversion && (
                    <div style={{ padding: '6px 16px 10px', fontSize: 11, color: '#aaa' }}>
                      * CPA y ROI por fuente se calculan por atribución directa: la inversión de cada pieza publicitaria se asigna a la fuente configurada en dicha pieza.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </>)}

        {!loading && !data && pidSel && (
          <div className="box p-5 text-center" style={{ color: '#aaa' }}>
            <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 42, display: 'block', margin: '0 auto 14px', color: '#dee2e6' }} />
            <p style={{ fontSize: 15, fontWeight: 600 }}>Sin datos para este programa</p>
          </div>
        )}
        {error && (
          <div className="alert alert-warning m-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            {error}
          </div>
        )}
        </ErrorBoundary>
      </SpinnerLoading>

      {/* ── Bitácora de Marketing (criterio 25 HU-16 — vinculada a KPI afectado) ── */}
      {pidSel && (
        <div className="box mb-3">
          <div className="box-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e9ecef' }}>
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: '#6f42c118', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ color: C_P, fontSize: 13 }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>Bitácora de Marketing</div>
              <small className="text-muted" style={{ fontSize: 11 }}>tareas y acuerdos vinculados al KPI que afectan</small>
            </div>
          </div>
          <div className="box-body" style={{ padding: '16px 20px' }}>
            {/* Formulario nuevo registro */}
            <div className="row mb-3" style={{ gap: '10px 0' }}>
              <div className="col-md-2">
                <select className="form-control form-control-sm" value={bitForm.tipo} onChange={e => setBitForm(f => ({ ...f, tipo: e.target.value }))}>
                  <option value="tarea">Tarea</option>
                  <option value="acuerdo">Acuerdo</option>
                </select>
              </div>
              <div className="col-md-3">
                <select className="form-control form-control-sm" value={bitForm.kpi_afectado} onChange={e => setBitForm(f => ({ ...f, kpi_afectado: e.target.value }))}>
                  <option value="">KPI afectado (opcional)</option>
                  {kpisBitacora.map(k => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <input type="date" className="form-control form-control-sm" value={bitForm.fecha} onChange={e => setBitForm(f => ({ ...f, fecha: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <input type="text" className="form-control form-control-sm" placeholder="Descripción..." value={bitForm.contenido}
                  onChange={e => setBitForm(f => ({ ...f, contenido: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && guardarBitacora()} />
              </div>
              <div className="col-md-2">
                <button className="btn btn-sm btn-primary w-100" disabled={bitSaving || !bitForm.contenido.trim()} onClick={guardarBitacora}>
                  {bitSaving ? 'Guardando...' : 'Agregar'}
                </button>
              </div>
            </div>
            {bitMsg && (
              <div className={`alert ${bitMsg.ok ? 'alert-success' : 'alert-danger'} py-1 px-2 mb-2`} style={{ fontSize: 12 }}>{bitMsg.text}</div>
            )}

            {/* Lista */}
            {bitLoading ? (
              <p className="text-muted text-center py-2" style={{ fontSize: 12 }}>Cargando...</p>
            ) : bitacora.length === 0 ? (
              <p className="text-muted text-center py-2" style={{ fontSize: 12 }}>Sin registros todavía para este programa.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                  <thead>
                    <tr style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>
                      <th>Fecha</th><th>Tipo</th><th>Descripción</th><th>KPI afectado</th><th>Resultado posterior</th><th>Estado</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bitacora.map(b => (
                      <tr key={b.id} style={{ opacity: b.estado === 'archivado' ? 0.5 : 1 }}>
                        <td style={{ whiteSpace: 'nowrap', color: '#888' }}>{b.fecha?.slice(0, 10).split('-').reverse().join('/')}</td>
                        <td><Pill color={b.tipo === 'acuerdo' ? C_P : C_B} size={10}>{b.tipo}</Pill></td>
                        <td>{b.contenido}</td>
                        <td>{b.kpi_afectado ? <Pill color={C_A} size={10}>{b.kpi_afectado.replace(/_/g, ' ')}</Pill> : <span className="text-muted">—</span>}</td>
                        <td style={{ color: '#888', maxWidth: 200 }}>{b.resultado_posterior || '—'}</td>
                        <td>
                          <select className="form-control form-control-sm" style={{ fontSize: 11, padding: '2px 6px', width: 'auto' }}
                            value={b.estado} onChange={e => cambiarEstadoBit(b.id, e.target.value)}>
                            <option value="por_iniciar">Por iniciar</option>
                            <option value="completado">Completado</option>
                            <option value="archivado">Archivado</option>
                          </select>
                        </td>
                        <td>
                          <button className="btn btn-sm" style={{ color: C_R, fontSize: 12 }} onClick={() => eliminarBit(b.id)} title="Eliminar">
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal preview de pieza ── */}
      {modalPieza && (() => {
        const navList = filteredPiezas.length > 0 ? filteredPiezas : sortedPiezas
        const idx  = navList.indexOf(modalPieza)
        const prev = idx > 0 ? navList[idx - 1] : null
        const next = idx < navList.length - 1 ? navList[idx + 1] : null
        const hasVideo = modalPieza.video_url || modalPieza.embed_vimeo
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setModalPieza(null)}>
            <div style={{ background: '#1a1a1a', borderRadius: 14, padding: 24, width: '90%', maxWidth: 720, position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,.6)' }}
              onClick={e => e.stopPropagation()}>

              {/* Cerrar */}
              <button onClick={() => setModalPieza(null)}
                style={{ position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', color: '#aaa', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>

              {/* Título */}
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 14, marginBottom: 14, paddingRight: 24 }}>
                {modalPieza.nombre_anuncio}
                <span style={{ marginLeft: 8, fontSize: 11, color: '#888', fontWeight: 400 }}>{modalPieza.tipo_anuncio} · {modalPieza.pagina}</span>
              </div>

              {/* Contenido */}
              {modalPieza.embed_vimeo ? (
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 8, overflow: 'hidden' }}>
                  <iframe src={`https://player.vimeo.com/video/${modalPieza.embed_vimeo}?autoplay=1&color=ff6b00`}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                    allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={modalPieza.nombre_anuncio} />
                </div>
              ) : modalPieza.video_url ? (
                <video src={modalPieza.video_url} controls autoPlay
                  style={{ width: '100%', borderRadius: 8, maxHeight: 420, background: '#000' }} />
              ) : modalPieza.thumbnail_url ? (
                <img src={modalPieza.thumbnail_url} alt={modalPieza.nombre_anuncio}
                  style={{ width: '100%', borderRadius: 8, maxHeight: 420, objectFit: 'contain', background: '#000' }} />
              ) : (
                <div style={{ padding: 60, textAlign: 'center', color: '#555' }}>Sin vista previa disponible</div>
              )}

              {/* Stats rápidos */}
              <div style={{ display: 'flex', gap: 20, marginTop: 14, fontSize: 12, color: '#888', flexWrap: 'wrap' }}>
                <span>Inversión <strong style={{ color: '#fff' }}>S/ {modalPieza.inversion?.toFixed(2)}</strong></span>
                <span>Leads <strong style={{ color: '#fff' }}>{modalPieza.cantidad_leads}</strong></span>
                {modalPieza.costo_lead > 0 && <span>CPL <strong style={{ color: '#fff' }}>S/ {modalPieza.costo_lead}</strong></span>}
                {modalPieza.cpc > 0 && <span>CPC <strong style={{ color: '#fff' }}>S/ {modalPieza.cpc}</strong></span>}
                {modalPieza.cpm > 0 && <span>CPM <strong style={{ color: '#fff' }}>S/ {modalPieza.cpm}</strong></span>}
                {modalPieza.score !== null && <span>Score <strong style={{ color: '#fff' }}>{modalPieza.score}</strong></span>}
              </div>

              {/* 3 dimensiones HU-16: volumen/costo · calidad de lead · resultado comercial */}
              {modalPieza.leads_crm > 0 && (
                <div className="row mt-3 pt-3" style={{ borderTop: '1px solid #333' }}>
                  <div className="col-4">
                    <div style={{ fontSize: 9, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Calidad del lead</div>
                    <div style={{ fontSize: 11, color: '#ccc', marginBottom: 3 }}>Tasa respuesta: <strong style={{ color: '#fff' }}>{modalPieza.tasa_respuesta_pieza}%</strong></div>
                    <div style={{ fontSize: 11, color: '#ccc', marginBottom: 3 }}>Calidad lead: <strong style={{ color: '#fff' }}>{modalPieza.calidad_lead_pieza}%</strong></div>
                    <div style={{ fontSize: 11, color: '#ccc', marginBottom: 3 }}>No ubicados: <strong style={{ color: '#fff' }}>{modalPieza.no_ubicados_pieza}</strong></div>
                    <div style={{ fontSize: 11, color: '#ccc' }}>Renuentes: <strong style={{ color: '#fff' }}>{modalPieza.renuentes_pieza}</strong></div>
                  </div>
                  <div className="col-4">
                    <div style={{ fontSize: 9, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Resultado comercial</div>
                    <div style={{ fontSize: 11, color: '#ccc', marginBottom: 3 }}>Tratos: <strong style={{ color: '#fff' }}>{modalPieza.tratos_pieza}</strong></div>
                    <div style={{ fontSize: 11, color: '#ccc', marginBottom: 3 }}>Reservas: <strong style={{ color: '#fff' }}>{modalPieza.reservas_pieza}</strong></div>
                    <div style={{ fontSize: 11, color: '#ccc', marginBottom: 3 }}>Inscritos: <strong style={{ color: '#fff' }}>{modalPieza.inscritos_pieza}</strong></div>
                    <div style={{ fontSize: 11, color: '#ccc' }}>Conversión: <strong style={{ color: '#fff' }}>{modalPieza.conversion_pieza}%</strong></div>
                  </div>
                  <div className="col-4">
                    <div style={{ fontSize: 9, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Cierre</div>
                    {modalPieza.cpa_pieza > 0 && <div style={{ fontSize: 11, color: '#ccc', marginBottom: 3 }}>CPA pieza: <strong style={{ color: '#fff' }}>S/ {modalPieza.cpa_pieza}</strong></div>}
                    <div style={{ fontSize: 11, color: '#ccc', marginBottom: 3 }}>Maduración: <strong style={{ color: '#fff' }}>{modalPieza.maduracion_pieza != null ? `${modalPieza.maduracion_pieza}d` : 'S/D'}</strong></div>
                    {modalPieza.accion_degradada && (
                      <div style={{ fontSize: 10, color: C_R, marginTop: 6 }}>⚠ Buen engagement pero sin resultado comercial aún</div>
                    )}
                  </div>
                </div>
              )}

              {/* Histórico mensual de la pieza (criterio 22) — solo leads/inscritos con fecha CRM */}
              {(() => {
                const histPz = historicoPieza.filter(h => h.id_creative === modalPieza.id_creative)
                if (!histPz.length) return null
                return (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid #333' }}>
                    <div style={{ fontSize: 9, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Evolución mensual (leads / inscritos)</div>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                      {histPz.map((h, i) => (
                        <div key={i} style={{ fontSize: 11, color: '#ccc' }}>
                          <div style={{ color: '#888' }}>{mesLbl(h.mes)}</div>
                          <div><strong style={{ color: '#fff' }}>{h.leads}</strong> leads · <strong style={{ color: '#fff' }}>{h.inscritos}</strong> insc.</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })()}

              {/* Prev / Next */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <button onClick={() => prev && setModalPieza(prev)} disabled={!prev}
                  style={{ background: prev ? '#333' : 'transparent', border: 'none', color: prev ? '#fff' : '#444', borderRadius: 6, padding: '6px 14px', cursor: prev ? 'pointer' : 'default', fontSize: 13 }}>
                  ← Anterior
                </button>
                <span style={{ color: '#555', fontSize: 12, alignSelf: 'center' }}>{idx + 1} / {navList.length}</span>
                <button onClick={() => next && setModalPieza(next)} disabled={!next}
                  style={{ background: next ? '#333' : 'transparent', border: 'none', color: next ? '#fff' : '#444', borderRadius: 6, padding: '6px 14px', cursor: next ? 'pointer' : 'default', fontSize: 13 }}>
                  Siguiente →
                </button>
              </div>
            </div>
          </div>
        )
      })()}

    </Layout>
  )
}

export default ReporteKpisMarketing
